import { Avatar, Card, Divider, Grid, Group, Stack, Text } from "@mantine/core"
import { Icon } from "@iconify/react"
import { createColumnHelper } from "@tanstack/react-table"
import React from "react"
import { useNavigate } from "react-router-dom"
import { CreateSuperAdminTenantAction, UpdateSuperAdminTenantAction } from "src/components/superadmin/tenants/SuperAdminTenantActions"
import { makeEnumBadgeComponent } from "src/components/ui/Badges"
import { DataTable } from "src/components/ui/DataTable"
import { LoadMoreButton } from "src/components/ui/LoadMoreButton"
import { EmptyPageComponent } from "src/components/ui/Misc"
import { ModuleErrors } from "src/components/ui/ModuleErrors"
import { PageFilters, usePageFilters } from "src/components/ui/PageFilters"
import { PageHeader } from "src/components/ui/PageHeader"
import { PageLayout } from "src/components/ui/PageLayout"
import { useActions, useAppState } from "src/state"
import { SetMetaInput } from "src/state/Module"
import { TenantFragment, Tenant_Status_Enum, Tenant_Type_Enum, Tenants_Bool_Exp } from "src/state/gql/_types"
import { TENANT_STATUS_ENUM_PALETTE, TENANT_TYPE_ENUM_PALETTE } from "src/state/palettes"
import { formatDate } from "src/utils/dates"

const TenantStatusBadge = makeEnumBadgeComponent(Tenant_Status_Enum,'filled','lg', TENANT_STATUS_ENUM_PALETTE)
const TenantTypeBadge = makeEnumBadgeComponent(Tenant_Type_Enum,'outline','lg', TENANT_TYPE_ENUM_PALETTE)

export function SuperAdminTenants() {

    const navigation = useNavigate()
    const { itemsList, errors, meta, loadingMore, loading, meta: { limit, offset, sortKey, sortOrder } } = useAppState(state => state.tenants)
    const { 
        subscribeToItems, 
        unsubscribeFromItems, 
        getMoreItems, 
        resetModule, 
        setMeta,
    } = useActions().tenants
    const { view } = useAppState(state => state.ui)

    const {
        filters,
        filterParams
    } = usePageFilters<Tenants_Bool_Exp['_and'], TenantFragment>({
        baseFilters: [
            { type: { _eq: Tenant_Type_Enum.Agency } }
        ],
        clickFilters: [
            {
                _or: [
                    { status: { _eq: Tenant_Status_Enum.Active } }
                ]
            }
        ],
        searchFilterFields: ["name","tenant_number"],
        searchFilterRelations: {},
        clickFilterField: 'status',
        clickFiltersDisableDefaultAll: true,
        clickFilterDefault: 'active',
        clickFilterValues: {
            active: { label: 'Active', filter: [{ status: { _eq: Tenant_Status_Enum.Active } }] },
            inactive: { label: 'Inactive', filter: [{ status: { _eq: Tenant_Status_Enum.Inactive } }] },
            all: { label: 'All', filter: [] }
        }
    })

    React.useEffect(() => {
        subscribeToItems(filters);
        return () => {
            unsubscribeFromItems()
        }
    }, [filters, limit, offset, sortKey, sortOrder, subscribeToItems, unsubscribeFromItems, resetModule])

    const columnHelper = createColumnHelper<TenantFragment>()

    const columns = React.useMemo(() => [
        columnHelper.accessor('id', {
            header: 'Id'
        }),
        columnHelper.accessor('tenant_number', {
            header: '#'
        }),
        columnHelper.accessor(row => row.tenant_status, {
            id: 'status',
            meta: {
                relationKey: 'thread_status',
                sortKey: 'value'
            },
            header: 'Status',
            cell: row => <TenantStatusBadge status={row.getValue()!} />
        }),
        columnHelper.accessor(row => row.tenant_type, {
            id: 'type',
            meta: {
                relationKey: 'tenant_type',
                sortKey: 'value'
            },
            header: 'Type',
            cell: row => <TenantTypeBadge status={row.getValue()!} />
        }),
        columnHelper.accessor('name', {
            header: 'Name'
        }),
        columnHelper.accessor('city', {
            header: 'City'
        }),
        columnHelper.accessor('state', {
            header: 'State'
        }),
        columnHelper.accessor('phone', {
            header: 'Phone'
        })
    ], [columnHelper])

    const initialState = {
        columnVisibility: {
            id: false
        }
    }

    const handleMetaChange = (meta: SetMetaInput<TenantFragment>) => {
        setMeta(meta)
    }

    const handleSelect = (id: string) => {
        navigation(`/tenants/${id}`)
    }

    const renderCards = () => {
        return (
            <React.Fragment>
                <Grid>
                    {itemsList.map((i, k) => {
                        const typeColor = i.type ? TENANT_TYPE_ENUM_PALETTE[i.type] : 'blue.5'
                        const initials = i.name ? i.name.charAt(0).toUpperCase() : '?'
                        const location = [i.city, i.state].filter(Boolean).join(', ')
                        return (
                            <Grid.Col span={{ base: 12, sm: 6, lg: 4 }} key={k}>
                                <Card
                                    onClick={() => handleSelect(i.id)}
                                    shadow="sm"
                                    padding="lg"
                                    radius="md"
                                    className="card-theme-global"
                                    h="100%"
                                    style={{ cursor: 'pointer' }}
                                >
                                    <Group justify="space-between" align="flex-start" wrap="nowrap" mb="xs">
                                        <Group gap="md" wrap="nowrap" style={{ minWidth: 0 }}>
                                            <Avatar
                                                src={i.logo || null}
                                                size="lg"
                                                radius="md"
                                                color={typeColor}
                                                style={{ flexShrink: 0 }}
                                            >
                                                {initials}
                                            </Avatar>
                                            <Stack gap={2} style={{ minWidth: 0 }}>
                                                <Text fw={700} size="md" lineClamp={1}>{i.name}</Text>
                                                <Text size="xs" c="dimmed">#{i.tenant_number}</Text>
                                            </Stack>
                                        </Group>
                                        {i.tenant_status && <TenantStatusBadge status={i.tenant_status} />}
                                    </Group>

                                    {i.tenant_type && (
                                        <Group mb="md">
                                            <TenantTypeBadge status={i.tenant_type} />
                                        </Group>
                                    )}

                                    <Divider mb="sm" />

                                    <Stack gap="xs">
                                        {location && (
                                            <Group gap="xs" wrap="nowrap">
                                                <Icon icon="ph:map-pin-duotone" width={15} style={{ flexShrink: 0, opacity: 0.6 }} />
                                                <Text size="sm" c="dimmed" lineClamp={1}>{location}</Text>
                                            </Group>
                                        )}
                                        {i.phone && (
                                            <Group gap="xs" wrap="nowrap">
                                                <Icon icon="ph:phone-duotone" width={15} style={{ flexShrink: 0, opacity: 0.6 }} />
                                                <Text size="sm" c="dimmed">{i.phone}</Text>
                                            </Group>
                                        )}
                                        {i.website && (
                                            <Group gap="xs" wrap="nowrap">
                                                <Icon icon="ph:globe-duotone" width={15} style={{ flexShrink: 0, opacity: 0.6 }} />
                                                <Text size="sm" c="dimmed" lineClamp={1}>{i.website.replace(/^https?:\/\//, '')}</Text>
                                            </Group>
                                        )}
                                        {i.instagram && (
                                            <Group gap="xs" wrap="nowrap">
                                                <Icon icon="ph:instagram-logo-duotone" width={15} style={{ flexShrink: 0, opacity: 0.6 }} />
                                                <Text size="sm" c="dimmed" lineClamp={1}>{i.instagram}</Text>
                                            </Group>
                                        )}
                                    </Stack>

                                    {(i.poc || i.anniversary) && (
                                        <>
                                            <Divider mt="sm" mb="xs" />
                                            <Group justify="space-between">
                                                {i.poc && <Text size="xs" c="dimmed">POC: {i.poc}</Text>}
                                                {i.anniversary && <Text size="xs" c="dimmed">Est. {formatDate(i.anniversary, 'MMM YYYY')}</Text>}
                                            </Group>
                                        </>
                                    )}
                                </Card>
                            </Grid.Col>
                        )
                    })}
                </Grid>
                <LoadMoreButton meta={meta} onLoadMore={getMoreItems} loading={loadingMore} />
            </React.Fragment>
        )
    }

    const renderList = () => {
        return (
            <DataTable<TenantFragment>
                columns={columns}
                data={itemsList}
                loading={loading}
                meta={meta}
                onMetaChange={handleMetaChange}
                initialState={initialState}
                onSelectItem={handleSelect}
            />
        )
    }

    return (
        <PageLayout
            header={
                <PageHeader
                    title={`Tenants`}
                    renderAction={
                        <Group>
                            <UpdateSuperAdminTenantAction />
                            <CreateSuperAdminTenantAction/>
                        </Group>
                    }
                />
            }
        >
            <PageFilters options={filterParams} />
            <ModuleErrors errors={errors} />
            {itemsList.length ? (<> {view === 'list' ? renderList() : renderCards()} </>) : (<EmptyPageComponent label='Tenants' />)}
        </PageLayout>
    )

}